import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ContactForm from '../components/ContactForm';
import { Mail, MapPin, Clock } from 'lucide-react';
import { SacredSection, ChristianIcon } from '../components/ChristianElements';

const Contact: React.FC = () => {
  const { t } = useTranslation();

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.info.address.title'),
      content: t('contact.info.address.content'),
      description: t('contact.info.address.description')
    },

    {
      icon: Mail,
      title: t('contact.info.email.title'),
      content: t('contact.info.email.content'),
      description: t('contact.info.email.description')
    },
    {
      icon: Clock,
      title: t('contact.info.responseTime.title'),
      content: t('contact.info.responseTime.content'),
      description: t('contact.info.responseTime.description')
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('meta.contact.title')}</title>
        <meta 
          name="description" 
          content={t('meta.contact.description')} 
        />
        <meta name="keywords" content={t('meta.contact.keywords')} />
        <link rel="canonical" href={t('meta.contact.canonical')} />
        
        {/* Open Graph */}
        <meta property="og:title" content={t('meta.contact.ogTitle')} />
        <meta property="og:description" content={t('meta.contact.ogDescription')} />
        <meta property="og:type" content={t('meta.contact.ogType')} />
        <meta property="og:url" content={t('meta.contact.ogUrl')} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-20 md:pt-24 pb-12 md:pb-16 bg-primary-50 holy-land-bg">
        <div className="container-max">
          <div className="max-w-3xl mx-auto text-center px-4">
            <div className="mb-4 md:mb-6">
              <ChristianIcon icon="dove" size="lg" className="mx-auto text-gold-600" />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-scripture font-bold text-primary-800 mb-4 md:mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 px-2">
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <SacredSection className="section-padding bg-white" title={t('contact.sendMessage')}>
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 px-4">
            {/* Contact Form */}
            <div>
              <div className="mb-4 md:mb-6">
                <ChristianIcon icon="cross" size="md" className="mb-4" />
              </div>
              <h2 className="text-xl md:text-2xl font-scripture font-bold text-primary-800 mb-4 md:mb-6">
                {t('contact.sendMessage')}
              </h2>
              <ContactForm />
            </div>

            {/* Contact Information */}
            <div>
              <div className="mb-4 md:mb-6">
                <ChristianIcon icon="dove" size="md" className="mb-4" />
              </div>
              <h2 className="text-xl md:text-2xl font-scripture font-bold text-primary-800 mb-4 md:mb-6">
                {t('contact.contactInfo')}
              </h2>
              
              <div className="space-y-4 md:space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-3 md:space-x-4 rtl:space-x-reverse">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 md:w-6 md:h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-base md:text-lg font-semibold text-primary-800 mb-1">
                        {info.title}
                      </h3>
                      <p className="text-primary-600 font-medium mb-1 text-sm md:text-base">
                        {info.content}
                      </p>
                      <p className="text-gray-600 text-xs md:text-sm">
                        {info.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Additional Information */}
              <div className="mt-6 md:mt-8 p-4 md:p-6 bg-primary-50 rounded-lg">
                <h3 className="text-base md:text-lg font-semibold text-primary-800 mb-2 md:mb-3">
                  {t('contact.whatToExpect')}
                </h3>
                <ul className="space-y-1 md:space-y-2 text-sm md:text-base text-gray-600">
                  {(t('contact.expectations', { returnObjects: true }) as string[]).map((expectation: string, index: number) => (
                    <li key={index} className="flex items-start space-x-2 rtl:space-x-reverse">
                      <span className="text-primary-600 mt-1">•</span>
                      <span>{expectation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </SacredSection>

      {/* FAQ Preview */}
      {/* <section className="section-padding bg-primary-50">
        <div className="container-max">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-primary-800 mb-4 md:mb-6">
              {t('contact.faqPreview.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8">
              {t('contact.faqPreview.subtitle')}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 text-left">
              {(t('contact.faqPreview.questions', { returnObjects: true }) as Array<{question: string, answer: string}>).map((faq, index) => (
                <div key={index} className="bg-white p-4 md:p-6 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-primary-800 mb-2 text-sm md:text-base">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 text-xs md:text-sm">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="mt-6 md:mt-8">
              <a
                href={t('common.links.faq')}
                className="btn-primary inline-flex items-center text-base md:text-lg px-6 md:px-8 py-3 md:py-4"
              >
{t('contact.faqPreview.viewAllFaqs')}
              </a>
            </div>
          </div>
        </div>
      </section> */}
    </>
  );
};

export default Contact;
